/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import { IconX } from "@tabler/icons-react";
import FileUploader from "../../../components/common/FileUploader";
import { useLanguage } from "../../../context/LanguageContext";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  useMerchantBulkDocMutation,
  useMerchantListData,
  useFetchMerchantTemplate,
} from "../../../apis/merchantApis";
import {
  styleAndProtectDummyRow,
  cleanUploadedExcelFile,
} from "../../../utilities/bulkUploadHelper";
type StaffBulkUploadModalsProps = {
  onClose: () => void;
  onResponseOpene: () => void;
  setStaffBulkResponse: React.Dispatch<React.SetStateAction<any>>;
  onBulkUploadSuccess?: () => void;
  // 👆 accepts a value or updater function
};
export default function MerchantBulkUploadModals({
  onClose,
  onResponseOpene,
  setStaffBulkResponse,
  onBulkUploadSuccess,
}: StaffBulkUploadModalsProps) {
  const { t } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(1);

  const { mutate: uploadDocMutate, isPending: isBulkUploading } =
    useMerchantBulkDocMutation();
  const { data: templateData, refetch: fetchTemplates } =
    useFetchMerchantTemplate();

  const {
    data,
    isPending: listPending,
    isFetching, // 👈 add this
    refetch,
  } = useMerchantListData(pageNo, limit, searchFor);

  const handleUploadDoc = async () => {
    if (!file) {
      toast.error(t("Please select a file to upload"));
      return;
    }
    const cleanedFile = await cleanUploadedExcelFile(file, templateData?.[0]);
    uploadDocMutate(
      { lmClient: cleanedFile },
      {
        onSuccess: (data: any) => {
          setStaffBulkResponse(data.data);
          // onResponseOpene();
          // onBulkUploadSuccess && onBulkUploadSuccess();
          onClose();
        },
        onError: (error: any) => {
          console.log(error, "error");
          toast.error(`${error}`);
        },
      }
    );
  };

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Merchant Template");

    // Row 1: Info
    const infoRow = worksheet.addRow([
      "This template is for importing Merchant data. Fields in red are mandatory; others are optional. Please do not modify the first 4 rows (Row 4 is sample/dummy data).",
    ]);
    worksheet.mergeCells(`A1:O1`);
    infoRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E2EFD9" },
      };
      cell.font = {
        name: "Arial",
        size: 10,
        bold: true,
        color: { argb: "212121" },
      };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } }, // black
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });

    infoRow.height = 25; // height in points

    // Row 2: Validation row
    const validationRow = worksheet.addRow([
      "Full name is required",
      "Phone (10–12 digits) is required",
      "Valid email is required",
      "Contact Person Name required",
      "Contact Method (Phone, Email & Whatsapp)",
      "Warehouse is required",
      "Payment Term (Daily, Weekly & Monthly)",
      "Access Type (LM, FM & Both)",
      "Merchant Type is required",
      "Short Address is required",
      "Full Address is required",
      "Vat Number (15 digits) is required",
      "CR Number (10 digits) is required",
      "National Id (10 digits) is required",
      "Contract Date (YYYY-MM-DD) is required",
    ]);
    validationRow.eachCell((cell) => {
      let fillColor = "FFCCCC"; // default light red

      if (Number(cell.col) === 5) {
        fillColor = "FFEB9C"; // light yellow
      }
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: fillColor },
      };

      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };

      cell.font = { color: { argb: "212121" } };

      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } }, // black
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });
    validationRow.height = 60; // height in points

    // Row 3: Headings
    const headings = Object.keys(templateData[0] || {}); // dynamic
    const headingsRow = worksheet.addRow(headings);

    headingsRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "217447" },
      };
      cell.font = { name: "Arial", bold: true, color: { argb: "ffffff" } };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } }, // black
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });
    headingsRow.height = 25; // height in points

    // Row 4: Dummy Data Row
    let dummyRow: ExcelJS.Row | undefined;
    if (templateData && templateData.length > 0 && templateData[0]) {
      const dummyValues = Object.values(templateData[0]);
      dummyRow = worksheet.addRow(dummyValues);
      dummyRow.eachCell((cell: any) => {
        cell.font = { name: "Arial", size: 10, color: { argb: "212121" } };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      dummyRow.height = 20;
    }

    // Optional: adjust column widths dynamically
    worksheet.columns = headings.map(() => ({ width: 25 }));

    // Adjust column widths
    worksheet.columns = [
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
    ];

    await styleAndProtectDummyRow(worksheet, dummyRow);

    // Export
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "merchant-template.xlsx");
  };

  // const handleDownloadTemplate = () => {
  //   if (templateData && templateData.length > 0) {
  //     try {
  //       const headings = Object.keys(templateData[0]);
  //       const csvContent = headings.join(",") + "\n";

  //       const blob = new Blob([csvContent], {
  //         type: "text/csv;charset=utf-8;",
  //       });
  //       const url = URL.createObjectURL(blob);

  //       const link = document.createElement("a");
  //       link.href = url;
  //       link.setAttribute("download", "clients-template.csv");
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);

  //       toast.success("CSV template downloaded successfully");
  //     } catch (err) {
  //       console.error("CSV generation error:", err);
  //       toast.error("Failed to generate CSV template");
  //     }
  //   } else {
  //     toast.error("Template not available");
  //   }
  // };

  useEffect(() => {
    fetchTemplates();
  }, []);

  return (
    <div className="relative bg-white rounded-3xl shadow-sm border flex flex-col overflow-hidden h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <h2 className="text-xl font-semibold text-[#212121]">
          {t("Bulk Upload Merchant")}
        </h2>

        <div className="flex items-center gap-3">
          <button
            onClick={handleUploadDoc}
            disabled={isBulkUploading}
            className="px-4 py-2 bg-[#FBD037] text-[#212121] rounded-lg"
          >
            {isBulkUploading ? t("Uploading...") : t("Submit")}
          </button>

          <button
            onClick={onClose}
            className="p-2 rounded-lg border border-[#D2D2D2]"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      <div className="space-y-4 p-6">
        <FileUploader
          name="photo"
          img="/img/upload-img.png"
          title={t("Upload Photo")}
          message={t("Upload a CSV file only (Max size: 2 MB).")}
          classNameMain="bg-transparent border-none flex items-center gap-3"
          fileVal={file}
          setFileVal={(file: any) => {
            setFile(file);
          }}
          showPreview={true}
          required
        />

        {file && (
          <p className="text-sm text-green-600 font-medium">
            {t("Selected File:")} {file.name}
          </p>
        )}

        <ul className="list-disc px-5 text-sm text-[#1E293B] font-medium space-y-1">
          <li>{t("Download the Excel/CSV template before uploading.")}</li>
          <li>{t("Do not change column headers.")}</li>
          <li>{t("Mandatory fields: Sender, Receiver, Reference, etc.")}</li>
        </ul>

        <div className="flex flex-wrap items-center justify-between gap-4 bg-[#fff] px-4 py-2 rounded-md border border-[#D2D2D2]">
          <p className="text-xs text-[#212121] font-medium">
            <b>{t("Note")}</b> : {t("Please upload the file in the same format as the downloaded file.")}
          </p>
          <button
            onClick={handleDownloadTemplate}
            className="bg-[#000000] text-white flex items-center space-x-2 px-4 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap hover:bg-black/90 transition-colors"
          >
            <img src="/img/csvxl.svg" alt="" />
            <span>{t("Download")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

